/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { ServiceData } from '@/types/sanity';
import { TimeSlots } from './TimeSlots';
import { PersonalInfoForm } from './PersonalInfoForm';
import { PaymentSummary } from './PaymentSummary';
import { ArrowRight, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/services/Badge';
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { format, parse, parseISO } from 'date-fns';
import { CalendarExport } from './CalendarExport';
import { EnterpriseLeadForm } from './EnterpriseLeadForm';
import { sendToHubSpot } from '@/utils/hubspot';

interface BookingFlowProps {
	isOpen: boolean;
	onClose: () => void;
	service: ServiceData;
}

// Update BookingStep type to include lead-form
type BookingStep =
	| 'lead-form'
	| 'datetime'
	| 'personal'
	| 'payment'
	| 'payment-result'
	| 'confirmation';

const formatLocalDateString = (date: Date): string =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function BookingFlow({ isOpen, onClose, service }: BookingFlowProps) {
	const [deliveryMode, setDeliveryMode] = React.useState<'online' | 'inPerson'>(
		'online',
	);

	const [step, setStep] = React.useState<BookingStep>(
		service.isPOA ? 'lead-form' : 'datetime',
	);
	const [selectedDate, setSelectedDate] = React.useState<Date>();
	const [selectedTime, setSelectedTime] = React.useState<string>();
	const [bookingData, setBookingData] = React.useState({
		fullName: '',
		email: '', // Added email for calendar invite
		phone: '',
		company: '', // Added company for lead form
		coachingReason: '',
		challenges: '',
		currentRole: '',
		publicSpeakingGoal: '',
		industry: '',
		requestProposal: false,
		deliveryMode: 'online',
		location: {
			city: '',
			state: '',
			country: '',
		},
	} as any);
	const [bookingConfirmed, setBookingConfirmed] = React.useState(false);
	const [calendarEventCreated, setCalendarEventCreated] = React.useState(false);
	const [paymentStatus, setPaymentStatus] = React.useState<
		'idle' | 'pending' | 'success' | 'failed'
	>('idle');
	const [paymentError, setPaymentError] = React.useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	// Store booking to Sanity
	const storeBookingToSanity = async (
		currentBookingData: any,
		bookingType: 'paid' | 'free' | 'enterprise',
		paymentInfo?: {
			reference: string;
			amount: number;
			status: string;
			date: string;
		},
	) => {
		const baseurl =
			process.env.NODE_ENV === 'production'
				? 'https://yaap.co.za/.netlify/functions'
				: '/.netlify/functions';

		try {
			const bookingPayload = {
				bookingReference:
					paymentInfo?.reference ||
					`${bookingType}-${service._id}-${Date.now()}`,
				bookingType,
				status: 'confirmed',

				// Service info
				serviceId: service._id,
				serviceTitle: service.title,
				servicePrice: paymentInfo?.amount || service.price,
				deliveryMode: currentBookingData.deliveryMode,

				// Customer info
				fullName: currentBookingData.fullName,
				email: currentBookingData.email,
				phone: currentBookingData.phone,
				company: currentBookingData.company,
				location: currentBookingData.location,

				// Schedule
				...(selectedDate && {
					selectedDate: selectedDate.toISOString(),
				}),
				selectedTime: selectedTime,

				// Personal info (coaching services)
				coachingReason: currentBookingData.coachingReason,
				challenges: currentBookingData.challenges,
				currentRole: currentBookingData.currentRole,
				publicSpeakingGoal: currentBookingData.publicSpeakingGoal,

				// Enterprise/POA fields
				requestProposal: currentBookingData.requestProposal,
				industry: currentBookingData.industry,
				address: currentBookingData.address,
				participants: currentBookingData.participants,
				customParticipants: currentBookingData.customParticipants,
				participantLevel: currentBookingData.participantLevel,
				customLevel: currentBookingData.customLevel,
				communicationSituations: currentBookingData.communicationSituations,
				communicationGoals: currentBookingData.communicationGoals,
				otherGoal: currentBookingData.otherGoal,
				trainingDate: currentBookingData.trainingDate,
				trainingFormat: currentBookingData.trainingFormat,
				duration: currentBookingData.duration,
				customDuration: currentBookingData.customDuration,
				customLocation: currentBookingData.customLocation,
				additionalInfo: currentBookingData.additionalInfo,
				budget: currentBookingData.budget,
				referralSource: currentBookingData.referralSource,
				customReferral: currentBookingData.customReferral,

				// Payment info
				...(paymentInfo && {
					paymentReference: paymentInfo.reference,
					paymentAmount: paymentInfo.amount,
					paymentStatus: paymentInfo.status,
					paymentDate: paymentInfo.date,
				}),

				// Calendar info will be updated later
				calendarEventCreated: false,
			};

			console.log(
				'Storing booking to Sanity:',
				bookingPayload.bookingReference,
			);

			const response = await fetch(`${baseurl}/create-booking`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(bookingPayload),
			});

			const result = await response.json();

			if (result.success) {
				console.log('Booking stored successfully:', result.bookingId);
				return result.bookingId;
			} else {
				console.error('Failed to store booking:', result.message);
				return null;
			}
		} catch (error) {
			console.error('Error storing booking to Sanity:', error);
			return null;
		}
	};

	const createCalendarEvent = async (currentBookingData: any) => {
		if (!currentBookingData.email) return; // No email, no invite

		const baseurl =
			process.env.NODE_ENV === 'production'
				? 'https://yaap.co.za/.netlify/functions'
				: '/.netlify/functions';

		// For isPOA, date and time might not be selected.
		// The backend will handle this case.
		const eventDate = selectedDate ? formatLocalDateString(selectedDate) : '';
		const eventTime = selectedTime || '';

		const eventDuration = service.requiresConsultation
			? 45
			: service.format === 'Hour' || service.format === 'Hours'
				? service.duration * 60
				: service.duration || 60;

		const response = await fetch(`${baseurl}/create-event`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				title: `Yaap Consultation: ${service.title}`,
				description: `Consultation request for ${service.title}.\n\nClient: ${currentBookingData.fullName}\nEmail: ${currentBookingData.email}\nPhone: ${currentBookingData.phone}\nCompany: ${currentBookingData.company}`,
				date: eventDate,
				timeSlot: eventTime,
				attendeeEmail: currentBookingData.email,
				duration: eventDuration,
				type: 'service', // Keep as 'service' for now
				serviceId: service._id,
			}),
		});

		const result = await response.json();
		setCalendarEventCreated(result.success);
	};

	const handleLeadFormSubmit = (data: any) => {
		const updatedBookingData = { ...bookingData, ...data };
		setBookingData(updatedBookingData);
		setStep('datetime');
	};

	// function for proposal confirmation
	const handleProposalConfirmation = async () => {
		// Store booking to Sanity first (enterprise type)
		await storeBookingToSanity(bookingData, 'enterprise');

		await sendPersonalInfoToHubSpot(bookingData);
		await sendAdminAlertEmail(bookingData);
		setBookingConfirmed(true);
		setStep('confirmation');
	};

	// Send booking data to the hubspot
	const sendPersonalInfoToHubSpot = async (currentBookingData: any) => {
		try {
			await sendToHubSpot(
				{
					properties: [
						{ property: 'firstname', value: currentBookingData.fullName },
						{ property: 'email', value: currentBookingData.email },
						{ property: 'phone', value: currentBookingData.phone },
						{ property: 'company', value: currentBookingData.company },
						{ property: 'city', value: currentBookingData?.location?.city },
						{ property: 'state', value: currentBookingData?.location?.state },
						{
							property: 'country',
							value: currentBookingData?.location?.country,
						},
						{ property: 'recent_service_booked', value: service.title },
					],
				},
				6, // Booking Leads ID (Hubspot)
			);
		} catch (error) {
			console.error('Failed to send data to HubSpot:', error);
		}
	};

	// Creates a calendar event for a confirmed booking. Isolated so a calendar
	// failure (e.g. Google credential issues) never blocks HubSpot/email steps.
	const createServiceCalendarEvent = async (title: string) => {
		try {
			const baseurl =
				process.env.NODE_ENV === 'production'
					? 'https://yaap.co.za/.netlify/functions'
					: '/.netlify/functions';

			const eventDuration = service.requiresConsultation
				? 45
				: service.format === 'Hour' || service.format === 'Hours'
					? service.duration * 60
					: service.duration || 60;

			const response = await fetch(`${baseurl}/create-event`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					title,
					description: `${service.description}\n\nClient: ${bookingData.fullName}\nPhone: ${bookingData.phone}\nDelivery Mode: ${bookingData.deliveryMode === 'inPerson' ? 'In Person' : 'Online'}`,
					date: selectedDate ? formatLocalDateString(selectedDate) : '',
					timeSlot: selectedTime,
					attendeeEmail: bookingData.email,
					duration: eventDuration,
					type: 'service',
					serviceId: service._id,
					deliveryMode: bookingData.deliveryMode,
				}),
			});

			if (!response.ok) {
				console.error(
					'create-event returned an error status:',
					response.status,
				);
				setCalendarEventCreated(false);
				return;
			}

			const result = await response.json();
			setCalendarEventCreated(Boolean(result.success));
		} catch (error) {
			// Never let a calendar failure block downstream notifications.
			console.error('Failed to create calendar event:', error);
			setCalendarEventCreated(false);
		}
	};

	// handlePaymentResult function to use 60-minute duration for consultation services
	const handlePaymentResult = async (success: boolean, message?: string) => {
		try {
			setPaymentStatus(success ? 'success' : 'failed');

			if (message) {
				setPaymentError(message);
			}

			// Move to payment result step first
			setStep('payment-result');

			// If payment was successful, store booking and create calendar event
			if (success && selectedDate && selectedTime && bookingData.email) {
				// Store booking to Sanity first (paid type)
				await storeBookingToSanity(bookingData, 'paid', {
					reference: localStorage.getItem('yaap_pending_booking')
						? JSON.parse(localStorage.getItem('yaap_pending_booking') || '{}')
								.reference
						: `paid-${service._id}-${Date.now()}`,
					amount:
						service.inPersonPrice && bookingData.deliveryMode === 'inPerson'
							? service.inPersonPriceValue
							: service.price,
					status: 'success',
					date: new Date().toISOString(),
				});

				await createServiceCalendarEvent(`Yaap Session: ${service.title}`);
			}
			await sendPersonalInfoToHubSpot(bookingData);
			await sendAdminAlertEmail(bookingData);
		} catch (error) {
			console.error('Failed during post-payment processing:', error);
		}
	};

	const handleBookingConfirmation = async () => {
		setIsSubmitting(true);
		try {
			// Store booking to Sanity first (free type)
			await storeBookingToSanity(bookingData, 'free');

			if (selectedDate && selectedTime && bookingData.email) {
				await createServiceCalendarEvent(`Yaap Session: ${service.title}`);
			}
			await sendPersonalInfoToHubSpot(bookingData);
			await sendAdminAlertEmail(bookingData);
			setBookingConfirmed(true);
			setStep('confirmation');
		} catch (error) {
			console.error('Failed during booking confirmation:', error);
			setCalendarEventCreated(false);
			// Still confirm the booking - it was already stored in Sanity above.
			setBookingConfirmed(true);
			setStep('confirmation');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleProceedAfterPaymentResult = () => {
		if (paymentStatus === 'success') {
			setBookingConfirmed(true);
			setStep('confirmation');
		} else {
			setStep('payment');
			setPaymentStatus('idle');
			setPaymentError(null);
		}
	};

	const handleProceedAfterLeadForm = async () => {
		try {
			await handleBookingConfirmation();
		} catch (error) {
			console.error('Error during booking confirmation:', error);
		}

		try {
			await sendPersonalInfoToHubSpot(bookingData);
		} catch (error) {
			console.error('Error sending personal info to HubSpot:', error);
		}

		try {
			await sendAdminAlertEmail(bookingData);
		} catch (error) {
			console.error('Error sending admin alert email:', error);
		}

		setBookingConfirmed(true);
		setStep('confirmation');
	};

	// Send email to info email for booking alert
	const sendAdminAlertEmail = async (currentBookingData: any) => {
		const baseurl =
			process.env.NODE_ENV === 'production'
				? 'https://yaap.co.za/.netlify/functions'
				: '/.netlify/functions';

		const address = currentBookingData.requestProposal
			? currentBookingData.address
			: `${currentBookingData.location.city}, ${currentBookingData.location.state}, ${currentBookingData.location.country}`;

		let emailBody = `
        <p>A new booking has been scheduled:</p>
        <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif;">
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Service:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${service.title}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Full Name:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.fullName}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Email:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.email}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Phone:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.phone}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Current Role:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.currentRole}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Company:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.company}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Location:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.coachingReason}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Challenges:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.challenges}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Public Speaking Goal:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.publicSpeakingGoal}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Address:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${address}</td>
            </tr>
			<tr>
    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Delivery Mode:</strong></td>
    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.deliveryMode === 'inPerson' ? 'In Person' : 'Online'}</td>
</tr>
            ${
							selectedDate && selectedTime
								? `
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Date:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${format(selectedDate, 'EEEE, MMMM d, yyyy')}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Time:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${selectedTime}</td>
            </tr>
            `
								: ''
						}
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Request Proposal:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.requestProposal ? 'Yes' : 'No'}</td>
            </tr>
        </table>
    `;

		if (currentBookingData.requestProposal) {
			emailBody += `
            <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; margin-top: 20px;">
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Industry:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.industry}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Address:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.address}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Participants:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.participants}</td>
                </tr>
                ${
									currentBookingData.customParticipants
										? `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Custom Participants:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.customParticipants}</td>
                </tr>
                `
										: ''
								}
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Participant Level:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.participantLevel}</td>
                </tr>
                ${
									currentBookingData.customLevel
										? `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Custom Level:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.customLevel}</td>
                </tr>
                `
										: ''
								}
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Communication Situations:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.communicationSituations.join(', ')}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Challenges:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.challenges}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Communication Goals:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.communicationGoals.join(', ')}</td>
                </tr>
                ${
									currentBookingData.otherGoal
										? `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Other Goal:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.otherGoal}</td>
                </tr>
                `
										: ''
								}
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Training Date:</strong></td>
															<td style="border: 1px solid #ddd; padding: 8px;">${(() => {
																if (!currentBookingData.trainingDate)
																	return 'N/A';
																let dateObj = currentBookingData.trainingDate;
																if (typeof dateObj === 'string') {
																	try {
																		const [d, m, y] = dateObj.split('/');
																		if (d && m && y) {
																			dateObj = parse(
																				dateObj,
																				'dd/MM/yyyy',
																				new Date(),
																			);
																		} else {
																			dateObj = parseISO(dateObj);
																		}
																	} catch (e) {
																		return 'N/A';
																	}
																}
																if (
																	dateObj instanceof Date &&
																	!isNaN(dateObj.getTime())
																) {
																	return format(dateObj, 'EEEE, MMMM d, yyyy');
																}
																return 'N/A';
															})()}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Training Format:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.trainingFormat}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Duration:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.duration}</td>
                </tr>
                ${
									currentBookingData.customDuration
										? `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Custom Duration:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.customDuration}</td>
                </tr>
                `
										: ''
								}
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Catering:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.catering}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Additional Info:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.additionalInfo}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Budget:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.budget}</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Referral Source:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.referralSource}</td>
                </tr>
                ${
									currentBookingData.customReferral
										? `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;"><strong>Custom Referral:</strong></td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${currentBookingData.customReferral}</td>
                </tr>
                `
										: ''
								}
            </table>
        `;
		}

		try {
			await fetch(`${baseurl}/email-service`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					type: 'sendEmail',
					to: 'bbndaba@yaap.co.za',
					cc: 'info@yaap.co.za',
					subject: 'Yaap Website: New Booking Scheduled',
					body: emailBody,
				}),
			});
		} catch (error) {
			console.error('Failed to send admin alert email:', error);
		}
	};

	const ServiceDetails = () => (
		<div className='space-y-4'>
			<div className='space-y-4'>
				<h4 className='text-2xl font-semibold'>{service.title}</h4>
				<div className='flex gap-2'>
					<Badge text={service.frequency} />
					<Badge text={service.location} />
				</div>
				<div className='mt-6 text-base leading-5 text-neutral-800'>
					{service.description}
				</div>

				{/* Add delivery mode selection if service has inPersonPrice */}
				{service.inPersonPrice && (
					<div className='space-y-2 mt-4'>
						<p className='text-sm font-medium text-gray-500'>DELIVERY MODE</p>
						<div className='flex gap-2'>
							<Button
								variant={deliveryMode === 'online' ? 'default' : 'outline'}
								size='sm'
								onClick={() => {
									setDeliveryMode('online');
									setBookingData({ ...bookingData, deliveryMode: 'online' });
								}}
								className={
									deliveryMode === 'online'
										? 'bg-[#fbb040] hover:bg-[#f8c880]'
										: ''
								}
							>
								Online
							</Button>
							<Button
								variant={deliveryMode === 'inPerson' ? 'default' : 'outline'}
								size='sm'
								onClick={() => {
									setDeliveryMode('inPerson');
									setBookingData({ ...bookingData, deliveryMode: 'inPerson' });
								}}
								className={
									deliveryMode === 'inPerson'
										? 'bg-[#fbb040] hover:bg-[#f8c880]'
										: ''
								}
							>
								In Person
							</Button>
						</div>
					</div>
				)}

				<div className='space-y-1'>
					<p className='text-sm font-medium text-gray-500'>PRICE</p>
					{service.isPOA ? (
						<p className='text-3xl font-bold'>Request Quote</p>
					) : service.requiresConsultation ? (
						<>
							<p className='text-3xl font-bold'>R{service.consultationPrice}</p>
							{service.consultationPrice && (
								<p className='text-sm mt-1'>
									You are paying the initial consultation fee. The package price
									will be discussed during the consultation.
								</p>
							)}
						</>
					) : (
						// Update price display based on delivery mode
						<p className='text-3xl font-bold'>
							R
							{service.inPersonPrice && deliveryMode === 'inPerson'
								? service.inPersonPriceValue
								: service.price}
						</p>
					)}
				</div>
			</div>
		</div>
	);

	const renderStepContent = () => {
		switch (step) {
			case 'lead-form':
				return (
					<EnterpriseLeadForm
						data={bookingData as any}
						onSubmit={handleLeadFormSubmit as any}
						isSubmitting={isSubmitting}
					/>
				);

			case 'datetime':
				return (
					<div className='space-y-8'>
						<SheetHeader className='p-0'>
							<SheetTitle className='text-3xl'>Let's take</SheetTitle>
							<SheetTitle className='text-3xl'>the first step.</SheetTitle>
							<p className='text-sm text-gray-500'>Select a date and time.</p>
						</SheetHeader>

						<div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
							<div className='space-y-6 md:pr-4'>
								<ServiceDetails />
							</div>

							<div className='md:px-4'>
								<h3 className='text-lg font-medium mb-4'>Select a date</h3>
								<div className='w-full'>
									<Calendar
										mode='single'
										selected={selectedDate}
										onSelect={setSelectedDate}
										className='w-full rounded-md border p-3'
										disabled={(date) => {
											const today = new Date();
											today.setHours(0, 0, 0, 0);

											// Log the raw value from Sanity to debug
											console.log(
												'Raw bookingStartDate:',
												service.bookingStartDate,
											);

											// Ensure proper date parsing
											let startDate = null;
											if (service.bookingStartDate) {
												startDate = new Date(service.bookingStartDate);
												// Reset hours to start of day for accurate comparison
												startDate.setHours(0, 0, 0, 0);
											}

											// Log the parsed date for verification
											console.log('Parsed startDate:', startDate);

											const effectiveMinDate =
												startDate && startDate > today ? startDate : today;

											// Log the final comparison date
											console.log('Effective min date:', effectiveMinDate);

											const isDisabled =
												date < effectiveMinDate ||
												date.getDay() === 0 ||
												date.getDay() === 6;

											// Log the comparison for specific dates
											console.log(
												`Date ${date} disabled? ${isDisabled} (compared to ${effectiveMinDate})`,
											);

											return isDisabled;
										}}
										classNames={{
											months: 'w-full',
											month: 'w-full',
											table: 'w-full',
											head_row: 'w-full',
											row: 'w-full',
											cell: 'w-[14.28%] text-center p-0',
											day: 'w-full h-9 font-normal aria-selected:opacity-100',
											nav_button:
												'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
										}}
									/>
								</div>
							</div>

							<div className='md:pl-4'>
								<h3 className='text-lg font-medium mb-4'>Time</h3>
								<TimeSlots
									selectedTime={selectedTime}
									onTimeSelect={setSelectedTime}
									selectedDate={selectedDate}
									serviceDuration={
										service.requiresConsultation
											? 45
											: service.format === 'Hour' || service.format === 'Hours'
												? service.duration * 60
												: service.duration || 60
									}
								/>
							</div>
						</div>

						<div className='flex justify-end pt-6'>
							<Button
								onClick={
									service.isPOA
										? handleBookingConfirmation
										: () => setStep('personal')
								}
								disabled={!selectedDate || !selectedTime || isSubmitting}
								className='bg-[#fbb040] hover:bg-[#f8c880] rounded-full px-6'
							>
								{isSubmitting && service.isPOA ? (
									<>
										<Loader2 className='mr-2 h-4 w-4 animate-spin' />
										Submitting...
									</>
								) : service.isPOA ? (
									'Submit'
								) : (
									'Continue'
								)}
								<ArrowRight className='ml-2 h-4 w-4' />
							</Button>
						</div>
					</div>
				);

			case 'personal':
				return (
					<PersonalInfoForm
						data={bookingData}
						onSubmit={(data: typeof bookingData) => {
							// Preserve the deliveryMode when updating booking data
							setBookingData({
								...data,
								deliveryMode: bookingData.deliveryMode,
							});
							setStep('payment');
						}}
						onBack={() => setStep('datetime')}
						isSubmitting={isSubmitting}
					/>
				);

			case 'payment':
				return (
					<PaymentSummary
						service={service}
						bookingData={bookingData}
						selectedDate={selectedDate!}
						selectedTime={selectedTime!}
						onBack={() => setStep('personal')}
						onPayment={async (success: boolean, message?: string) => {
							setIsSubmitting(true);
							// Update to pass success/failure status
							await handlePaymentResult(success, message);
							setIsSubmitting(false);
						}}
						isSubmitting={isSubmitting}
					/>
				);

			case 'payment-result':
				return (
					<div className='space-y-6 w-full max-w-3xl'>
						<div>
							<h2 className='text-2xl font-semibold'>
								{paymentStatus === 'success'
									? 'Payment Complete'
									: 'Payment Failed'}
							</h2>
							<p className='text-sm text-gray-500'>
								{paymentStatus === 'success'
									? 'Your payment was processed successfully'
									: 'There was a problem processing your payment'}
							</p>
						</div>

						<Card>
							<CardHeader>
								<div className='flex items-center gap-3'>
									{paymentStatus === 'success' ? (
										<CheckCircle2 className='h-6 w-6 text-green-600' />
									) : (
										<AlertCircle className='h-6 w-6 text-red-600' />
									)}
									<CardTitle
										className={
											paymentStatus === 'success'
												? 'text-green-700'
												: 'text-red-700'
										}
									>
										{paymentStatus === 'success'
											? 'Transaction Successful'
											: 'Transaction Failed'}
									</CardTitle>
								</div>
							</CardHeader>
							<CardContent className='space-y-4'>
								<div className='space-y-2'>
									<h3 className='font-medium'>Booking Details</h3>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Service</span>
										<span className='font-medium'>{service.title}</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Date</span>
										<span className='font-medium'>
											{selectedDate
												? format(selectedDate, 'EEEE, MMMM d, yyyy')
												: ''}
										</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Time</span>
										<span className='font-medium'>{selectedTime}</span>
									</div>
								</div>

								<Separator />

								<div className='space-y-2'>
									{paymentStatus === 'success' ? (
										<>
											<p>Thank you for booking {service.title}.</p>
											<p className='text-sm'>
												Your transaction has been completed successfully.
												{calendarEventCreated
													? " We've sent you a calendar invitation."
													: " We'll send you a confirmation email shortly."}
											</p>
										</>
									) : (
										<>
											<p>We couldn't process your payment at this time.</p>
											<p className='text-sm text-red-600'>
												{paymentError || 'Please try again or contact support.'}
											</p>
										</>
									)}
								</div>
							</CardContent>
							<CardFooter className='border-t pt-4'>
								<Button
									onClick={handleProceedAfterPaymentResult}
									className={`w-full ${
										paymentStatus === 'success'
											? 'bg-[#fbb040] hover:bg-[#f8c880]'
											: 'bg-gray-400 hover:bg-gray-500'
									}`}
								>
									{paymentStatus === 'success' ? 'Continue' : 'Try Again'}
								</Button>
							</CardFooter>
						</Card>
					</div>
				);

			case 'confirmation':
				return (
					<div className='space-y-6 w-full max-w-3xl'>
						<div>
							<h2 className='text-2xl font-semibold'>
								{bookingData.requestProposal
									? 'Request Submitted'
									: 'Booking Confirmed'}
							</h2>
							<p className='text-sm text-gray-500'>
								{bookingData.requestProposal
									? 'Your training proposal request has been successfully submitted'
									: 'Your session has been successfully scheduled'}
							</p>
						</div>

						<Card>
							<CardHeader>
								<div className='flex items-center gap-3'>
									<CheckCircle2 className='h-6 w-6 text-green-600' />
									<CardTitle className='text-green-700'>
										{bookingData.requestProposal
											? 'Request Complete'
											: 'Booking Complete'}
									</CardTitle>
								</div>
							</CardHeader>
							<CardContent className='space-y-4'>
								{/* Service Details */}
								<div className='space-y-2'>
									<h3 className='font-medium'>Service Details</h3>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Service</span>
										<span className='font-medium'>{service.title}</span>
									</div>
									{!bookingData.requestProposal && selectedDate && (
										<>
											<div className='flex justify-between text-sm'>
												<span className='text-gray-500'>Date</span>
												<span className='font-medium'>
													{format(selectedDate, 'EEEE, MMMM d, yyyy')}
												</span>
											</div>
											<div className='flex justify-between text-sm'>
												<span className='text-gray-500'>Time</span>
												<span className='font-medium'>{selectedTime}</span>
											</div>
											<div className='flex justify-between text-sm'>
												<span className='text-gray-500'>Duration</span>
												<span className='font-medium'>
													{service.duration === 0
														? 'Tailored'
														: service.duration + ' ' + service.format}
												</span>
											</div>
											{service.inPersonPrice && (
												<div className='flex justify-between text-sm'>
													<span className='text-gray-500'>Delivery Mode</span>
													<span className='font-medium'>
														{bookingData.deliveryMode === 'inPerson'
															? 'In Person'
															: 'Online'}
													</span>
												</div>
											)}
										</>
									)}
								</div>

								<Separator />

								{/* Personal Details */}
								<div className='space-y-2'>
									<h3 className='font-medium'>Personal Information</h3>
									<div className='text-sm space-y-1'>
										<p>{bookingData.fullName}</p>
										<p>{bookingData.phone}</p>
										<p>{bookingData.email}</p>
									</div>
								</div>

								{bookingData.requestProposal ? (
									<>
										<Separator />
										<div className='rounded-md bg-green-50 p-3 text-sm'>
											<p className='flex items-center gap-2 text-green-700'>
												<CheckCircle2 className='h-4 w-4' />
												Thank you for your training proposal request. Our team
												will review your requirements and contact you shortly
												with a customized proposal.
											</p>
										</div>
									</>
								) : (
									<>
										{calendarEventCreated && (
											<>
												<Separator />
												<div className='rounded-md bg-green-50 p-3 text-sm'>
													<p className='flex items-center gap-2 text-green-700'>
														<CheckCircle2 className='h-4 w-4' />A calendar
														invitation has been sent to your email:{' '}
														{bookingData.email}
													</p>
												</div>
											</>
										)}

										<Separator />

										{/* Calendar Export Options - only show for regular bookings */}
										{selectedDate && selectedTime && (
											<CalendarExport
												title={`Yaap: ${service.title}`}
												description={`${service.description}\n\nBooking for: ${bookingData.fullName}\nPhone: ${bookingData.phone}`}
												startDate={selectedDate}
												startTime={selectedTime}
												duration={service.duration || 60}
												location={service.location}
											/>
										)}
									</>
								)}
							</CardContent>
							<CardFooter className='border-t pt-4'>
								<Button
									onClick={onClose}
									className='w-full bg-[#fbb040] hover:bg-[#f8c880]'
								>
									Close
								</Button>
							</CardFooter>
						</Card>

						<div className='text-sm text-gray-500 text-center'>
							<p>Thank you for choosing our services.</p>
							{bookingData.requestProposal ? (
								<p>
									We'll be in touch soon with your custom training proposal!
								</p>
							) : (
								<p>We look forward to seeing you!</p>
							)}
						</div>
					</div>
				);
		}
	};

	return (
		<Sheet open={isOpen} onOpenChange={onClose}>
			<SheetContent
				side='right'
				className='w-full max-w-[100vw] sm:max-w-[100vw] p-8 overflow-y-auto md:px-25'
			>
				<div className='flex w-full mx-auto justify-center'>
					{renderStepContent()}
				</div>
			</SheetContent>
		</Sheet>
	);
}
